import axios from 'axios';
import config from '../config';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export const setupAuthInterceptors = (axiosInstance = axios) => {
  // Request Interceptor: Attach access token to headers
  axiosInstance.interceptors.request.use(
    (reqConfig) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (token && reqConfig.headers) {
        reqConfig.headers.Authorization = `Bearer ${token}`;
      }
      return reqConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response Interceptor: Catch 401 and refresh access token
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (!originalRequest) {
        return Promise.reject(error);
      }

      // Avoid looping if the refresh request itself returns 401
      if (originalRequest.url && originalRequest.url.includes('/auth/refresh-token')) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const baseUrl = config.API_BASE_URL || 'https://greenyellow-nightingale-241896.hostingersite.com';
          const response = await axios.post(`${baseUrl}/api/auth/refresh-token`, {
            refreshToken,
          });

          const newToken = response.data.token || response.data.accessToken;
          const newRefreshToken = response.data.refreshToken || refreshToken;

          if (newToken) {
            localStorage.setItem('token', newToken);
            localStorage.setItem('authToken', newToken);
          }
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          processQueue(null, newToken);
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);

          console.error('Refresh token expired or invalid. Logging out.');
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userInfo');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

export default setupAuthInterceptors;
